import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { useTheme } from '@/components/theme-provider';
import { Sun, Moon } from 'lucide-react';
import LanguageSelector from '@/components/ui/LanguageSelector';
import { useTranslation } from '@/hooks/useTranslation';

type AuthLayoutProps = {
  children: ReactNode;
};

export default function AuthLayout({ children }: AuthLayoutProps) {
  const { theme, setTheme } = useTheme();
  const { t } = useTranslation();

  const toggleTheme = () => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  };

  return (
    <div className="min-h-screen bg-background flex flex-col justify-center py-8 sm:py-12 px-4 sm:px-6 lg:px-8">
      {/* Language Selector and Theme Toggle */}
      <div className="absolute top-4 right-4 sm:right-6 lg:right-8 flex items-center space-x-2 z-40">
        <div className="relative z-50">
          <LanguageSelector variant="navbar" showLabel={false} />
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleTheme}
          className="rounded-full"
          aria-label="Toggle theme"
        >
          {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
        </Button>
      </div>

      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <Link to="/">
          <h2 className="mt-6 text-center text-3xl font-extrabold text-foreground">
            DeepTrade
          </h2>
        </Link>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-foreground">
          {t('auth.layout.signInToAccount')}
        </h2>
        <p className="mt-2 text-center text-sm text-muted-foreground">
          {t('auth.layout.or')}{' '}
          <Link to="/register" className="font-medium text-primary hover:text-primary/80">
            {t('auth.layout.createNewAccount')}
          </Link>
        </p>
      </div>

      <div className="mt-6 sm:mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-card py-6 sm:py-8 px-4 sm:px-6 lg:px-10 shadow sm:rounded-lg">
          {children}
        </div>
      </div>
    </div>
  );
}
